import axios, { AxiosError, AxiosInstance } from 'axios'
import type { App } from 'vue'
import AppConfig from '@/config'
import { classifyLimit, normalizeApiError, type LimitKind, type LimitResource } from './errors'
import { guestHeader } from '@/utils/guestId'

export interface InterceptorHooks {
	onUnauthorized: () => void | Promise<void>
	onNetworkError: () => void
	// resource приходит только с лимитом тарифа (402) и выбирает вариант шторки Pro.
	onLimitExceeded: (kind: LimitKind, resource?: LimitResource) => void
	isPollingActive: () => boolean
}

let hooks: InterceptorHooks = {
	onUnauthorized: () => {},
	onNetworkError: () => {},
	onLimitExceeded: () => {},
	isPollingActive: () => false
}

export function setInterceptorHooks(next: InterceptorHooks): void {
	hooks = next
}

export function getInterceptorHooks(): InterceptorHooks {
	return hooks
}

export const http: AxiosInstance = axios.create({
	baseURL: AppConfig.apiUrl
})

// guest_id уезжает и cookie, и заголовком — бэк читает оба канала.
http.interceptors.request.use((config) => {
	Object.entries(guestHeader()).forEach(([name, value]) => {
		config.headers.set(name, value)
	})
	// Ответы API не должны читаться из HTTP-кэша браузера. Бэк отдаёт их без запрета на
	// кэширование, а дубль вкладки (и восстановление сессии) грузит страницу с предпочтением
	// кэша — GET /usage тогда возвращает срез из прошлой загрузки, и UsageBar показывает
	// остаток, который человек уже потратил. Запрос до сервера при этом вообще не доходит.
	// no-cache, а не no-store: копию хранить не жалко, важно, что перед выдачей она
	// перепроверяется у сервера. Тела POST/PUT браузер не кэширует — трогаем только чтение.
	if ((config.method ?? 'get').toLowerCase() === 'get') {
		config.headers.set('Cache-Control', 'no-cache')
	}
	return config
})

const excludedRoutes = ['/eula', '/privacy-policy', '/cookie-policy', '/subswait']

http.interceptors.response.use(
	(response) => response,
	async (error: AxiosError) => {
		const apiError = normalizeApiError(error)
		const limit = classifyLimit(apiError)
		// Лимит тоже приходит с 403 — его нельзя перепутать с «нет такого треда».
		if (!limit && (error.status === 403 || error.status === 404) && error.config?.url?.includes('/threads/')) {
			window.location.replace('/notFounded')
			return
		}
		const currentUrl = window.location.pathname
		if (excludedRoutes.includes(currentUrl)) {
			return
		}
		if (error.status === 401) {
			await hooks.onUnauthorized()
		}
		// Ошибки авторизации показываем на самой странице входа: иначе 429 от /auth/send-code
		// открыл бы гостю модалку «Войти» поверх формы.
		const isAuthRequest = error.config?.url?.startsWith('/auth/')
		if (!isAuthRequest && limit) {
			hooks.onLimitExceeded(limit, apiError.resource)
		}
		if (error.code === 'ERR_NETWORK') {
			if (!hooks.isPollingActive()) {
				hooks.onNetworkError()
			}
		}
		throw apiError
	}
)

export default (app: App) => {
	app.axios = http
	app.$http = http
}
